import type { BookkeepingType } from "./types";

/**
 * Month-close checklist generation — the item library from
 * "Zournal_Month_Close_Checklist v1.0" (25 July 2026), turned into the
 * client's close TEMPLATE during onboarding. Reviewed once here; each month
 * the tenant copies it into a run. Seeds carry no completion state.
 *
 * Phase-1 gating decision: show every item in the library. The only
 * conditions applied are the ones this flow can already answer —
 *   - GST: registered or not, one repeat per GSTIN, registration type,
 *     e-invoicing, SEZ→LUT, filing frequency (from the gst-review answer)
 *   - banks: pre-close items repeat per bank account read from the books
 *   - first close: branched on the books-questions bookkeeping type
 *   - CFO items: on the client's chosen services
 * Everything the doc conditions on answers we don't collect yet (payroll,
 * fixed assets, loans, intercompany, cash ledger, Auto Pass, No-Bills
 * limit, multi-currency, suspense account, FBAC) is INCLUDED — the firm
 * deletes what doesn't apply, which beats silently hiding real close work.
 */

export type MonthCloseSection = "pre-close" | "first-close" | "area" | "close-out";

export interface MonthCloseItem {
  id: string;
  text: string;
  /** Verified by the system each month — can't be hand-ticked or removed. */
  auto: boolean;
  /** Added by the firm on this screen, not from the library. */
  custom?: boolean;
  /**
   * One line under the item saying why it is there, for items whose point
   * isn't obvious from the title. Travels to the tenant as task guidance.
   */
  hint?: string;
}

export interface MonthCloseGroup {
  id: string;
  section: MonthCloseSection;
  title: string;
  /** Small badge next to the group title, e.g. "First close only". */
  tag?: string;
  items: MonthCloseItem[];
}

/** What the step persists as the answer. */
export interface MonthCloseChecklistAnswer {
  generatedAt: string;
  groups: MonthCloseGroup[];
}

export type GstRegistrationType = "regular" | "composition" | "sez";
export type GstFilingFrequency = "monthly" | "qrmp" | "composition";

export interface MonthCloseGstin {
  /** GSTIN when Zoho holds one, else the branch id — stable across re-reads. */
  key: string;
  /** "27AABCU9603R1ZM (Maharashtra)" — empty string means "don't prefix". */
  label: string;
  registrationType?: GstRegistrationType;
  /**
   * Whether the IRN item joins this registration's close. Already resolved to
   * a boolean by the caller: the stored flag is nullable, and null is read as
   * off, because the reads that leave it null are ones where Zoho answered
   * "off" for an organisation whose payload named no registration.
   */
  eInvoicing: boolean;
  filingFrequency?: GstFilingFrequency;
}

export interface MonthCloseBank {
  id: string;
  /** "HDFC Bank ••1234" */
  label: string;
}

export interface MonthCloseContext {
  /** Which books this template is for. Tally skips the Zoho-only lines. */
  books: "zoho" | "tally";
  /**
   * false only when the books were read and say not registered — that is
   * the one GST fact we trust enough to hide the whole area. Unknown
   * (books not connected, read failed) shows the area generically.
   */
  gstRegistered: boolean;
  gstins: MonthCloseGstin[];
  banks: MonthCloseBank[];
  cfoModule: boolean;
  bookkeepingType?: BookkeepingType;
}

const item = (id: string, text: string, auto = false): MonthCloseItem => ({ id, text, auto });

// ── Section 1 — pre-close gate ──────────────────────────────────────────────

function buildPreCloseGroup(ctx: MonthCloseContext): MonthCloseGroup {
  const items: MonthCloseItem[] = [];

  if (ctx.banks.length > 0) {
    ctx.banks.forEach((bank) => {
      items.push(
        item(`pre-statement-${bank.id}`, `${bank.label} — statement uploaded`, true),
        item(`pre-categorised-${bank.id}`, `${bank.label} — all transactions for the period categorised`, true),
        item(`pre-reco-${bank.id}`, `${bank.label} — bank reconciliation: book balance equals statement closing balance`, true)
      );
    });
  } else {
    items.push(
      item("pre-statement", "Statement uploaded for every active bank account", true),
      item("pre-categorised", "All bank transactions for the period categorised", true),
      item("pre-reco", "Bank reconciliation — book balance equals statement closing balance", true)
    );
  }

  items.push(
    item("pre-suspense", "Suspense account balance is nil", true),
    item("pre-cash-count", "Cash in hand agrees to physical count")
  );

  return { id: "pre-close", section: "pre-close", title: "Pre-close gate", items };
}

// ── First close only ────────────────────────────────────────────────────────

function buildFirstCloseGroup(ctx: MonthCloseContext): MonthCloseGroup {
  const type = ctx.bookkeepingType;
  const newBooks = type === "new-zoho-opening-balances" || type === "new-company";
  const sameBooks = type === "existing-zoho-current";
  const catchUp = type === "catch-up";
  // Unknown bookkeeping type → show every branch rather than guess one.
  const unknown = type === undefined;

  const items: MonthCloseItem[] = [];
  if (newBooks || unknown) {
    items.push(
      item(
        "first-opening-tb",
        ctx.books === "tally"
          ? "Opening trial balance agrees to the source books"
          : "Opening trial balance agrees to source (Excel, Tally, prior Zoho)"
      ),
      item("first-opening-arap", "Opening AR and AP detail imported and tied to control accounts"),
      item("first-tax-opening", "Tax opening balances set as on the account start date")
    );
  }
  items.push(item("first-bank-opening", "Bank opening balances confirmed for every account"));
  if (ctx.books !== "tally" && (sameBooks || unknown)) {
    items.push(item("first-historical-bank", "Unmatched historical bank transactions cleared before bank sync"));
  }
  if (catchUp || unknown) {
    items.push(item("first-backlog", "Catch-up backlog fully processed", true));
  }
  items.push(item("first-fa-register", "Fixed asset register migrated with written-down value"));

  return { id: "first-close", section: "first-close", title: "First close", tag: "First close only", items };
}

// ── Section 2 — accounting areas ────────────────────────────────────────────

/**
 * Turning Locations on is a one-way switch in the Zoho UI that nothing in
 * Zournal can do for the client, and every GST registration the tenant knows
 * about arrives through it. Manual on purpose: the CA confirms what they see
 * in Zoho, and requires_proof makes them show it.
 */
const locationsEnabledItem = (): MonthCloseItem => ({
  id: "gst-locations-enabled",
  text: "Locations enabled in Zoho Books — one location per GSTIN, primary marked (this cannot be undone in Zoho)",
  auto: false,
  hint: "Needed even with a single registration: the location list is how Zournal reads the primary GSTIN back from Zoho.",
});

function gstItemsFor(gstin: MonthCloseGstin): MonthCloseItem[] {
  const prefix = gstin.label ? `${gstin.label} — ` : "";
  const id = (suffix: string) => `gst-${gstin.key}-${suffix}`;
  const composition = gstin.registrationType === "composition" || gstin.filingFrequency === "composition";

  const items: MonthCloseItem[] = [item(id("outward"), `${prefix}outward supplies register ties to books`)];
  if (gstin.eInvoicing) {
    items.push(item(id("irn"), `${prefix}all B2B invoices carry a valid IRN`, true));
  }
  items.push(
    item(id("gstr2b"), `${prefix}GSTR-2B downloaded and reconciled to purchase register`),
    item(id("sec17-5"), `${prefix}ineligible credits under Sec 17(5) identified and reversed`),
    item(id("rule37"), `${prefix}Rule 37 reversal — invoices unpaid beyond 180 days`),
    item(id("rcm"), `${prefix}RCM liability identified, self-invoices raised, tax paid`)
  );
  if (composition) {
    items.push(item(id("cmp08"), `${prefix}CMP-08 filed`));
  } else {
    items.push(
      item(id("gstr1"), `${prefix}GSTR-1 / IFF filed`),
      item(id("gstr3b"), `${prefix}GSTR-3B filed and liability discharged`)
    );
  }
  items.push(item(id("ledgers"), `${prefix}cash and credit ledger balances tie to books`));
  if (gstin.registrationType === "sez") {
    items.push(item(id("lut"), `${prefix}LUT valid for the financial year`));
  }
  return items;
}

function buildAreaGroups(ctx: MonthCloseContext): MonthCloseGroup[] {
  const groups: MonthCloseGroup[] = [];

  groups.push({
    id: "area-revenue",
    section: "area",
    title: "Revenue",
    items: [
      item("rev-invoices-posted", "All invoices for the period posted, nothing stuck", true),
      item("rev-sales-register", "Revenue in books ties to the sales register"),
      item("rev-cutoff", "Revenue cut-off reviewed — unbilled and deferred revenue"),
      item("rev-credit-notes", "Credit notes and cancellations reviewed"),
      item("rev-variance", "Revenue variance reviewed against prior month"),
      item("rev-fx-revaluation", "Foreign currency invoices revalued at period-end rate"),
    ],
  });

  groups.push({
    id: "area-cogs",
    section: "area",
    title: "Cost of revenue",
    items: [
      item("cogs-bills-posted", "All bills for the period posted, nothing stuck", true),
      item("cogs-accruals", "Accruals raised for goods and services received but not billed"),
      item("cogs-cutoff", "Cost cut-off matched to revenue recognised"),
      item("cogs-gl-mapping", "Direct cost GL mapping reviewed for misposting"),
      item("cogs-autopass-sample", "Sample of Auto Pass posted bills reviewed"),
    ],
  });

  groups.push({
    id: "area-employee-cost",
    section: "area",
    title: "Employee cost",
    items: [
      item("emp-payroll-posted", "Payroll posted for the period"),
      item("emp-statutory", "PF, ESI and professional tax accrued and paid"),
      item("emp-salary-payable", "Salary payable ledger reconciled"),
      item("emp-advances", "Staff advances and reimbursements settled"),
    ],
  });

  groups.push({
    id: "area-other-expenses",
    section: "area",
    title: "Other expenses",
    items: [
      item("oexp-variance", "Expense variance reviewed against prior month"),
      item("oexp-unsupported", "Expenses without supporting documents reviewed"),
      item("oexp-nobills-review", "No Bills GL entries reviewed", true),
      item("oexp-nobills-limit", "No Bills GL entries within the configured per-bill limit", true),
      item("oexp-prepaid", "Prepaid portion identified and moved to prepaid"),
    ],
  });

  groups.push({
    id: "area-depreciation",
    section: "area",
    title: "Depreciation / PPE",
    items: [
      item("ppe-additions", "Additions and disposals for the period recorded"),
      item("ppe-depreciation", "Depreciation posted"),
      item("ppe-cwip", "Capital work in progress reviewed"),
      item("ppe-register", "Fixed asset register ties to books"),
    ],
  });

  if (ctx.gstRegistered) {
    const registrations: MonthCloseGstin[] =
      ctx.gstins.length > 0
        ? ctx.gstins
        : ctx.books === "tally"
          ? []
          : // Registered (or unknown) with nothing read back — one generic pass.
            //
            // `eInvoicing: true` here even though an unread flag on a KNOWN
            // registration now reads as off. Different question: there, Zoho
            // answered and the answer was off; here we have no registrations at
            // all, so nobody has said anything about e-invoicing and dropping
            // the IRN check would hide real close work behind a read that never
            // happened. Keeps the show-everything rule where it still holds.
            [{ key: "org", label: "", eInvoicing: true }];
    const items = [
      ...(ctx.books === "tally" ? [] : [locationsEnabledItem()]),
      ...registrations.flatMap(gstItemsFor),
    ];
    if (items.length > 0) {
      groups.push({
        id: "area-gst",
        section: "area",
        title: "GST",
        // Once per client, not per registration: Locations is an organisation
        // switch in Zoho, and it is the switch every per-GSTIN item below
        // depends on. First in the group because nothing under it is true
        // until it is done. Tally has no such switch, so that line is omitted.
        items,
      });
    }
  }

  groups.push({
    id: "area-tds",
    section: "area",
    title: "TDS",
    items: [
      item("tds-deducted", "TDS deducted on all applicable payments and credits"),
      item("tds-ldc", "Lower deduction certificates validated and applied"),
      item("tds-paid", "TDS liability paid within the due date"),
      item("tds-challan-mapped", "Challan recorded and mapped to the TDS ledgers", true),
      item("tds-payable-ties", "TDS payable ledger ties to challans paid", true),
      item("tds-return", "Quarterly TDS return filed"),
      item("tcs-collected", "TCS collected, recorded and paid"),
      item("tcs-27eq", "Form 27EQ filed"),
    ],
  });

  groups.push({
    id: "area-vendor-advance",
    section: "area",
    title: "Vendor advance",
    items: [
      item("vadv-aged", "Debit balances in creditors listed and aged", true),
      item("vadv-adjusted", "Advances adjusted against bills where possible"),
      item("vadv-recovery", "Long-outstanding advances flagged for recovery"),
    ],
  });

  const customerAdvance: MonthCloseItem[] = [
    item("cadv-aged", "Credit balances in debtors listed and aged", true),
    item("cadv-adjusted", "Advances adjusted against invoices where possible"),
  ];
  if (ctx.gstRegistered) customerAdvance.push(item("cadv-gst", "GST on advances checked"));
  groups.push({ id: "area-customer-advance", section: "area", title: "Customer advance", items: customerAdvance });

  const ar: MonthCloseItem[] = [
    item("ar-subledger", "AR subledger ties to the control account", true),
    item("ar-ageing", "AR ageing reviewed with action notes"),
    item("ar-unapplied", "Unapplied receipts cleared", true),
  ];
  if (ctx.cfoModule) ar.push(item("ar-doubtful", "Doubtful debt provision reviewed"));
  groups.push({ id: "area-ar", section: "area", title: "Accounts receivable", items: ar });

  groups.push({
    id: "area-ap",
    section: "area",
    title: "Accounts payable",
    items: [
      item("ap-subledger", "AP subledger ties to the control account", true),
      item("ap-ageing", "AP ageing reviewed with action notes"),
      item("ap-unapplied", "Unapplied payments cleared", true),
      item("ap-duplicates", "Duplicate bills checked", true),
    ],
  });

  const otherAssets: MonthCloseItem[] = [
    item("oa-prepaid", "Prepaid expenses amortised"),
    item("oa-deposits", "Deposits and advances confirmed"),
    item("oa-loans-given", "Loans and advances given reviewed"),
  ];
  if (ctx.gstRegistered) otherAssets.push(item("oa-itc-portal", "Input tax credit balance ties to the portal"));
  otherAssets.push(item("oa-intercompany", "Intercompany receivable agreed with counterparty"));
  groups.push({ id: "area-other-assets", section: "area", title: "Other assets", items: otherAssets });

  groups.push({
    id: "area-other-liabilities",
    section: "area",
    title: "Other liabilities",
    items: [
      item("ol-accruals", "Accruals raised for known unbilled costs"),
      item("ol-statutory", "Statutory dues other than GST and TDS reconciled and paid"),
      item("ol-loans", "Loan balances and interest accrued"),
      item("ol-provisions", "Provisions reviewed for release or top-up"),
      item("ol-intercompany", "Intercompany payable agreed with counterparty"),
    ],
  });

  return groups;
}

// ── Section 3 — close-out ───────────────────────────────────────────────────

function buildCloseOutGroup(ctx: MonthCloseContext): MonthCloseGroup {
  const items: MonthCloseItem[] = [
    item("out-queries", "All open queries resolved", true),
    item("out-fbac", "FBAC review complete", true),
    item("out-tb-review", "Trial balance reviewed for unusual balances", true),
  ];
  if (ctx.cfoModule) items.push(item("out-mis", "Mapped P&L / MIS pack generated"));
  items.push(
    item("out-client-ack", "Financials shared with the client and acknowledged"),
    item("out-manager-signoff", "Manager sign-off")
  );
  if (ctx.cfoModule) items.push(item("out-cfo-signoff", "CFO sign-off"));
  items.push(item("out-period-lock", "Period locked in the backend"));

  return { id: "close-out", section: "close-out", title: "Close-out", items };
}

export function generateMonthCloseGroups(ctx: MonthCloseContext): MonthCloseGroup[] {
  return [buildPreCloseGroup(ctx), buildFirstCloseGroup(ctx), ...buildAreaGroups(ctx), buildCloseOutGroup(ctx)].filter(
    (group) => group.items.length > 0
  );
}

export function countMonthCloseItems(groups: MonthCloseGroup[]): number {
  return groups.reduce((total, group) => total + group.items.length, 0);
}

/**
 * Regenerate from a fresh context while keeping what the firm typed in:
 * custom items re-attach to the regenerated group with the same id, and a
 * custom item whose group vanished (say GST after a re-read) is not lost —
 * it lands in the close-out group rather than silently disappearing.
 */
export function regenerateKeepingCustom(ctx: MonthCloseContext, previous: MonthCloseGroup[]): MonthCloseGroup[] {
  const groups = generateMonthCloseGroups(ctx);
  const byId = new Map(groups.map((group) => [group.id, group]));
  const fallback = groups[groups.length - 1];

  previous.forEach((oldGroup) => {
    const customs = oldGroup.items.filter((entry) => entry.custom);
    if (customs.length === 0) return;
    const target = byId.get(oldGroup.id) ?? fallback;
    target.items = [...target.items, ...customs];
  });

  return groups;
}
